import {AsyncPipe} from '@angular/common';
import {Component, forwardRef, inject, Injector, input, OnInit} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, Validator, Validators, NgControl, FormControlName, FormGroupDirective, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {Observable, of, Subscription} from 'rxjs';
import {catchError, debounceTime, map, startWith, switchMap} from 'rxjs/operators';
import {DynamicAutocompleteItem, DynamicAutocompleteResponse, DynamicAutocompleteService} from '../../service/dynamic-autocomplete/dynamic-autocomplete.service';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {autocompleteModelValidator} from 'src/infrastructure/helper/autocomplete-model-validator';

@AutoUnsubscribe()
@Component({
    selector: 'dynamic-autocomplete',
    templateUrl: 'dynamic-autocomplete.component.html',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule,
        MatButtonModule,
        AsyncPipe,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DynamicAutocompleteComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DynamicAutocompleteComponent),
            multi: true,
        },
    ]
})
export class DynamicAutocompleteComponent implements OnInit, ControlValueAccessor, Validator {
    public formLabel = input<string>('Autocomplete');
    public hideClearButton = input<boolean>(false);
    public dataTestId = input<string>('data-test-id');
    public maxResults = input<number>(25);
    public emitString = input<boolean>(false);
    public tableName = input.required<string>();
    public columnName = input.required<string>();
    public minCharacters = input<number>(0);
    
    protected Math = Math;
    
    private readonly SPECIAL_FIELDS = ['Branch', 'Bank'];
    private readonly CLIENT_TABLE_NAME = 'client';
    private readonly PROFILE_NAME_COLUMN = 'profilename';

    private readonly injector = inject(Injector);
    private readonly autocompleteService = inject(DynamicAutocompleteService);

    protected autocompleteInnerControl = new FormControl<DynamicAutocompleteItem | string | null>(null);

    protected options: DynamicAutocompleteItem[] = [];
    protected filteredOptions$?: Observable<DynamicAutocompleteItem[]>;
    protected inputTextEmit$?: Subscription;
    
    protected displayNameWith = (option: DynamicAutocompleteItem): string => 
        option ? this.formatDisplayValue(option) : '';
    
    private onChange!:(value: DynamicAutocompleteItem | string | null) => void;
    private onTouched!:(value: DynamicAutocompleteItem | string | null) => void;
    private formatDisplayValue(option: DynamicAutocompleteItem): string {
        if (option?.value && 
            this.tableName().toLowerCase() === this.CLIENT_TABLE_NAME &&
            this.columnName().toLowerCase() === this.PROFILE_NAME_COLUMN) {
            
            if (!option.originalValue) {
                option.originalValue = option.value;
            }
            
            const nameParts = option.value.split('/');
            if (nameParts.length === 2) {
                const firstName = this.capitalizeFirstLetter(nameParts[0].trim());
                const lastName = this.capitalizeFirstLetter(nameParts[1].trim());
                return `${firstName} ${lastName}`;
            }
            
            return this.capitalizeWords(option.value);
        }
        
        return option.value;
    }
    
    private capitalizeFirstLetter(text: string): string {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }
    
    private capitalizeWords(text: string): string {
        if (!text) return '';
        return text.split(' ')
            .map(word => this.capitalizeFirstLetter(word))
            .join(' ');
    }
    
    protected isSpecialField(): boolean {
        return this.SPECIAL_FIELDS.includes(this.tableName());
    }
    
    private getFormattedColumnName(columnName: string): string {
        return columnName.charAt(0).toUpperCase() + columnName.slice(1);
    }
    public ngOnInit(): void {
        this.filteredOptions$ = this.autocompleteInnerControl.valueChanges
        .pipe(
            startWith(''),
            debounceTime(200),
            switchMap(value => {
                if(value && typeof value === 'string' && value.trim().length > 0){
                    let actualTableName = this.tableName();
                    let actualColumnName = this.getFormattedColumnName(this.columnName());
                    
                    if (this.formLabel().includes('Trip Record Locator') && 
                        (window.location.pathname.includes('PaymentsReconciliation') || 
                         this.formLabel().includes('Reconciliation'))) {
                        actualTableName = 'ReconciliationView';
                        actualColumnName = 'AdxReference';
                    }
                    
                    const apiParams = {
                        tableName: actualTableName,
                        columnName: actualColumnName,
                        userInput: value,
                        maxResults: this.maxResults(),
                    };
                    
                    return this.autocompleteService.getOptions(apiParams)
                    .pipe(
                        map((data: DynamicAutocompleteResponse) => {
                            this.options = data.items;
                            return this.filterOptions(value || '');
                        }),
                        catchError((error: unknown) => {
                            console.error('Error in autocomplete:', error);
                            return of([] as DynamicAutocompleteItem[]);
                        })
                    );
                }
                this.options = [];
                return of([]);
            }),
        );
        
        this.inputTextEmit$ = this.autocompleteInnerControl.valueChanges.subscribe((event) => {
            if(this.emitString() && ((typeof event === 'string' && event?.length >= 1) || !event)) {
                this.setValue(event ? event : null);
            }
        });
        

        const parentControl = this.injector.get(NgControl) as FormControlName;
        const parentForm = this.injector.get(FormGroupDirective).getControl(parentControl);

        if(parentForm.hasValidator(Validators.required)){
            this.autocompleteInnerControl.setValidators([autocompleteModelValidator() , Validators.required]);
        }
    }

    protected isRequired(): boolean {
        return this.autocompleteInnerControl.hasValidator(Validators.required);
    }

    protected isObject(): boolean {
        return !(typeof this.autocompleteInnerControl.value === 'string');
    }
    protected hasEnoughChars(): boolean {
        return true;
    }
    private filterOptions(value: string): DynamicAutocompleteItem[] {
        if (!value) return this.options;
        
        const filterValue = value.toLowerCase().trim();
        if (!filterValue) return this.options;
        
        return this.options.filter(
            option => option.value && option.value.toLowerCase().includes(filterValue)
        ) as DynamicAutocompleteItem[];  
    }

    protected clearAutocomplete(componentRef: MatAutocomplete): void {
        componentRef.options.forEach((optionRef) => optionRef.deselect());
        this.options = [];
        this.autocompleteInnerControl.reset(null);
        this.onChange(null);
        this.onTouched(null);
    }

    public writeValue(value: DynamicAutocompleteItem | string | null): void {
        if (value && typeof value === 'string') {
            const autocompleteItem: DynamicAutocompleteItem = {
                value: value,
                originalValue: value
            };
            this.autocompleteInnerControl.patchValue(autocompleteItem);
        } else {
            this.autocompleteInnerControl.patchValue(value);
        }
        
        if (!value) {
            this.autocompleteInnerControl.reset();
        }
    }

    public registerOnChange(fn: (value: DynamicAutocompleteItem | string | null)=> void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: (value: DynamicAutocompleteItem | string | null)=> void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.autocompleteInnerControl[isDisabled? 'disable' : 'enable']();
    }

    public validate(control: AbstractControl): ValidationErrors | null {
        if(control.errors && !control.value){
            return {
                required: true,
            };
        }
        return null;
    }

    public setValue(value: DynamicAutocompleteItem | string | null): void {
        this.onChange(value);
        this.onTouched(value);
    }

    protected handleOptionSelected(selectedOption: DynamicAutocompleteItem): void {
        if (this.emitString()) {
            this.setValue(selectedOption?.value || null);
        } else {
            this.setValue(selectedOption);
        }
    }
}